import React from 'react';
import { Card, CardContent, Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: 'white',
  boxShadow: theme.shadows[2],
}));

const TableWrapper = styled(Paper)(({ theme }) => ({
  borderRadius: '8px',
  border: '1px solid #e2e8f0',
  overflowX: 'auto', // Important for horizontal scrolling
  overflowY: 'auto',
  boxShadow: 'none',
  width: '100%',
  position: 'relative',
}));

const StyledTable = styled(Table)(({ theme }) => ({
  minWidth: '100%',
  borderCollapse: 'collapse',
  tableLayout: 'auto', // Allow columns to size based on content but respect minWidth
  '& .MuiTableCell-root': {
    minWidth: '130px', // Apply minimum width to all cells
  },
}));

const StyledTableCell = styled(TableCell)(({ theme, isHeader, monthColor, isTotalRow, isClickable }) => ({
  padding: '12px 16px',
  fontSize: '0.875rem',
  whiteSpace: 'nowrap', // Prevent text wrapping
  minWidth: '130px', // Ensure minimum width per column
  
  ...(isHeader && {
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    backgroundColor: '#f8fafc',
    ...(monthColor === 'blue' && {
      backgroundColor: '#eff6ff',
      color: '#1e3a8a',
    }),
    ...(monthColor === 'amber' && {
      backgroundColor: '#fffbeb',
      color: '#92400e',
    }),
    ...(monthColor === 'emerald' && {
      backgroundColor: '#ecfdf5',
      color: '#065f46',
    }),
    ...(!monthColor && {
      backgroundColor: '#f8fafc',
      color: '#0f172a',
    }),
  }),
  
  ...(isTotalRow && {
    backgroundColor: '#f1f5f9',
    fontWeight: 600,
    color: '#0f172a',
  }),
  
  ...(isClickable && {
    color: '#4f46e5',
    fontWeight: 500,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#eef2ff',
      color: '#4338ca',
    },
  }),
  
  ...(!isTotalRow && !isClickable && {
    color: '#334155',
  }),
}));

const DataTable = ({ title, data, months, testId, onCellClick }) => {
  if (!data || data.length === 0) {
    return null;
  }

  const getMonthColor = (columnName) => {
    const lower = columnName.toLowerCase();
    if (lower.includes('cm-2') || lower.includes('cm2') || lower.includes('month-2') || lower.includes('month - 2')) {
      return 'blue';
    }
    if (lower.includes('cm-1') || lower.includes('cm1') || lower.includes('month-1') || lower.includes('month - 1')) {
      return 'amber';
    }
    if (lower.includes('cm)') || (lower.includes('current month') && !lower.includes('-'))) {
      return 'emerald';
    }
    return null;
  };

  const isNumericColumn = (col) => {
    const firstCol = Object.keys(data[0])[0];
    return col !== firstCol;
  };

  const isClickableCell = (col, value) => {
    return isNumericColumn(col) && typeof value === 'number' && value > 0;
  };

  const handleCellClick = (row, col, value) => {
    if (onCellClick && isClickableCell(col, value)) {
      const groupCol = Object.keys(data[0])[0];
      const groupValue = row[groupCol];
      onCellClick({
        tableTitle: title,
        groupColumn: groupCol,
        groupValue: groupValue,
        column: col,
        value: value
      });
    }
  };

  const columns = Object.keys(data[0]);

  return (
    <StyledCard data-testid={testId}>
      <CardContent sx={{ p: 2 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600, 
            color: '#1e293b', 
            mb: 3 
          }}
        >
          {title}
        </Typography>
        
        <TableWrapper>
          <StyledTable>
            <TableHead>
              <TableRow>
                {columns.map((col) => {
                  const monthColor = getMonthColor(col);
                  return (
                    <StyledTableCell
                      key={col}
                      isHeader={true}
                      monthColor={monthColor}
                      data-testid={`${testId}-header-${col}`}
                      sx={{
                        minWidth: '130px', // Explicit min width for header cells
                        whiteSpace: 'normal', // Allow header text to wrap if needed
                        wordBreak: 'break-word',
                        maxWidth: '200px', // Optional: set max width for headers
                      }}
                    >
                      {col}
                    </StyledTableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, idx) => {
                const firstColValue = row[columns[0]];
                const isTotalRow = firstColValue === 'TOTAL';
                
                return (
                  <TableRow 
                    key={idx}
                    sx={{
                      '&:hover': {
                        backgroundColor: !isTotalRow ? '#f8fafc' : 'inherit',
                      },
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    {columns.map((col) => {
                      const value = row[col];
                      const clickable = !isTotalRow && isClickableCell(col, value);
                      const monthColor = getMonthColor(col);
                      
                      return (
                        <StyledTableCell
                          key={col}
                          isTotalRow={isTotalRow}
                          isClickable={clickable}
                          monthColor={monthColor}
                          onClick={() => !isTotalRow && handleCellClick(row, col, value)}
                          data-testid={`${testId}-cell-${idx}-${col}`}
                          title={clickable ? 'Click to view details' : undefined}
                          sx={{
                            cursor: clickable ? 'pointer' : 'default',
                            minWidth: '130px', // Explicit min width for data cells
                          }}
                        >
                          {value !== null && value !== undefined ? value : '-'}
                        </StyledTableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </StyledTable>
        </TableWrapper>
      </CardContent>
    </StyledCard>
  );
};

export default DataTable;